/* eslint-disable jsx-a11y/click-events-have-key-events */

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { AnimatePresence, motion } from "framer-motion";
import {
  ElementType,
  FC,
  MouseEvent,
  MutableRefObject,
  useEffect,
  useRef,
} from "react";
import { useHistory } from "react-router";

import __ from "web/Layout/Translations";

import CloseIcon from "web/assets/icons/close.svg";

import { PropsWithClasses } from "web/types/Common";
import type { Nullable } from "web/types/Utils";

import classify from "web/classify";

import useModalFocus from "../utils/useModalFocus";
import defaultStyles from "./modal.scss";

const [initial, animate, exit] = [
  { opacity: 0, y: 200 },
  { opacity: 1, y: 0 },
  { opacity: 0, y: -200 },
];

interface IDesktopModalProps {
  component?: ElementType;
  closeAction?: () => void;
  modalRef: MutableRefObject<Nullable<HTMLDivElement>>;
  ariaLabel?: Nullable<string>;
  persistent?: Nullable<boolean>;
  fullscreen?: Nullable<boolean>;
}

type ExtendedCSSStyleDeclaration = CSSStyleDeclaration & {
  zoom?: string;
};

const DesktopModal: FC<PropsWithClasses<IDesktopModalProps>> = ({
  classes = {},
  component: Component = null,
  closeAction = () => {},
  modalRef,
  ariaLabel,
  persistent = false,
  fullscreen = false,
}) => {
  const modalContentRef = useRef<HTMLDivElement>(null);
  const history = useHistory();

  const documentClickHandler = (e: MouseEvent) => {
    const modalElement = modalContentRef.current;
    const clickedElement = e.target as HTMLElement;
    const scrollbarWidth =
      clickedElement.offsetWidth - clickedElement.clientWidth;

    // to not close modal when user clicks on a scrollbar with resolutions that set zoom property on root
    const rootStyles: ExtendedCSSStyleDeclaration = getComputedStyle(
      document.getElementById("root")!
    );
    const rootZoom = "zoom" in rootStyles ? Number(rootStyles.zoom) : 1;
    const adjustedClientX = e.clientX / rootZoom;

    const isScrollbarClicked =
      scrollbarWidth > 0 &&
      adjustedClientX > clickedElement.offsetWidth - scrollbarWidth;

    if (
      !persistent &&
      !modalElement?.contains(clickedElement as Node) &&
      !isScrollbarClicked
    ) {
      closeAction();
    }
  };

  useEffect(() => {
    return history.listen(() => {
      if (history.action === "POP") {
        setTimeout(() => {
          closeAction();
        }, 500);
      }
    });
  }, [history]);

  const { handleKeyDown } = useModalFocus({ component: Component, modalRef });

  return Component ? (
    <AnimatePresence>
      <motion.div
        className={classes.root}
        initial={initial}
        animate={animate}
        exit={exit}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        onMouseDown={documentClickHandler}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel || undefined}
        ref={modalRef}
      >
        <div
          className={`${classes.content}${fullscreen ? ` ${defaultStyles.fullscreen}` : ""}`}
          role="document"
          ref={modalContentRef}
        >
          {typeof Component === "function" ? (
            <Component closeAction={closeAction} />
          ) : (
            Component
          )}
          {!persistent && (
            <button
              type="button"
              className={classes.closeButton}
              onClick={closeAction}
              data-t1="close_modal_button"
              aria-label={__("Zamknij modal")}
            >
              <CloseIcon className={classes.closeIcon} />
            </button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  ) : null;
};

export default classify<PropsWithClasses<IDesktopModalProps>>(defaultStyles)(
  DesktopModal
);
